import { cn } from '@commenda/utils';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import React from 'react';

/**
 * IconButton variant styles using class-variance-authority (CVA).
 * Defines the visual appearance and behavior of icon buttons across different states.
 */
const iconButtonVariants = cva(
  'inline-flex justify-center items-center rounded-md text-center outline-none transition-colors disabled:pointer-events-none disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 shrink-0 disabled:pointer-events-none',
  {
    variants: {
      variant: {
        default:
          'bg-transparent hover:bg-button-tertiary-bg-hover disabled:bg-transparent text-button-tertiary-text hover:text-fg-brand disabled:text-button-tertiary-text-disabled focus-visible:ring-fg-tertiary',
        destructive:
          'bg-transparent hover:bg-button-tertiary-error-bg-hover disabled:bg-transparent text-button-tertiary-error-text hover:text-button-tertiary-error-text-hover disabled:text-button-tertiary-error-text-disabled focus-visible:ring-bg-error-solid',
      },
      size: {
        xs: 'size-5 py-xs px-xs',
        sm: 'size-6 py-xs px-xs',
        md: 'size-7 py-xs px-xs',
        lg: 'size-8 py-sm px-sm',
        xl: 'size-10 py-md px-md',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'md',
    },
  }
);

/**
 * Base icon button properties that extend HTML button attributes and include CVA variants.
 * This type provides the foundation for all icon button configurations.
 */
export type BaseIconButtonProps =
  React.ButtonHTMLAttributes<HTMLButtonElement> &
    VariantProps<typeof iconButtonVariants>;

/**
 * Icon button properties when using asChild pattern.
 * When asChild is true, the icon button renders as a Slot component, allowing it to be
 * rendered as any HTML element or React component.
 */
export type AsChildIconButtonProps = BaseIconButtonProps & {
  /** When true, renders the icon button as a Slot component instead of a button element */
  asChild: true;
  /** Icon element to display (optional when using asChild) */
  icon?: React.ReactNode;
};

/**
 * Extended icon button properties with additional features.
 * This type is used when asChild is false, enabling full icon button functionality.
 */
export type ExtendedIconButtonProps = BaseIconButtonProps & {
  /** When false or undefined, enables full icon button functionality */
  asChild?: false;

  /**
   * Icon element to display. Can be from popular libraries like react-icons,
   * custom JSX elements, or any React component.
   */
  icon: React.ReactNode;

  /**
   * Label for screen readers and accessibility.
   * Required for proper accessibility when no visible text is present.
   */
  'aria-label': string;
};

/**
 * Union type representing all possible icon button configurations.
 * The type system ensures proper component behavior across different configurations.
 */
export type IconButtonProps = ExtendedIconButtonProps | AsChildIconButtonProps;

/**
 * A specialized button component designed for icon-only interactions.
 * Perfect for toolbars, navigation, and compact interfaces.
 *
 * @example
 * ```tsx
 * // Basic usage with icon from Icons library
 * <IconButton size="sm" variant="default" icon={Icons.BarChart01} aria-label="View Chart" />
 *
 * // Destructive variant
 * <IconButton size="md" variant="destructive" icon={Icons.Trash} aria-label="Delete Item" />
 *
 * // With custom JSX icon
 * <IconButton size="lg" icon={<CustomIcon />} aria-label="Custom Action" />
 *
 * // As child (renders as anchor)
 * <IconButton asChild>
 *   <a href="/external">External</a>
 * </IconButton>
 * ```
 *
 * @param props - Icon button configuration properties
 * @param props.variant - Visual style variant (default: 'default')
 * @param props.size - Size variant (default: 'md')
 * @param props.icon - Icon element to display (required when not using asChild)
 * @param props.asChild - Whether to render as Slot component
 * @param props['aria-label'] - Accessibility label (required when not using asChild)
 * @param props.className - Additional CSS classes
 * @param props.children - Child element (when using asChild)
 * @param ref - Forwarded ref to the button element
 *
 * @returns A button element or Slot component based on asChild prop
 */
const IconButton = React.forwardRef<HTMLButtonElement, IconButtonProps>(
  ({ className, variant, size, asChild = false, icon, ...props }, ref) => {
    if (asChild) {
      return (
        <Slot
          data-variant={variant}
          data-size={size}
          className={cn(iconButtonVariants({ variant, size }), className)}
          ref={ref as React.Ref<any>}
          {...(props as AsChildIconButtonProps)}
        >
          {icon}
        </Slot>
      );
    }

    // Type assertion to access icon and aria-label when asChild is false
    const { 'aria-label': ariaLabel, ...restProps } =
      props as ExtendedIconButtonProps;

    return (
      <button
        className={cn(
          iconButtonVariants({ variant, size }),
          className,
          'group'
        )}
        ref={ref}
        aria-label={ariaLabel}
        data-variant={variant}
        data-size={size}
        type="button"
        {...restProps}
      >
        {icon}
      </button>
    );
  }
);

IconButton.displayName = 'IconButton';

export { IconButton, iconButtonVariants };
